// Concedii — the labels, colours and day arithmetic shared by every surface
// that shows leave: the page, the person tab, the calendar bands and the
// mobile port. Dependency-free and DOM-free on purpose (mobile imports it as
// `@web/lib/leaveConstants`), so no JSX and no icons live here.
import { translate } from './i18n';
import type { LeaveStatus, LeaveType, PublicHoliday } from './leaveApi';

/** Reading order everywhere a type is listed: most common first. */
export const LEAVE_TYPES: LeaveType[] = [
  'ANNUAL',
  'SICK',
  'UNPAID',
  'SPECIAL_EVENT',
  'SHORT_ABSENCE',
];

export const LEAVE_STATUSES: LeaveStatus[] = ['PENDING', 'APPROVED', 'REJECTED', 'CANCELLED'];

/**
 * Live label getters, so a language switch is reflected without re-creating
 * the constant — the same trick `taskConstants.PRIORITY_OPTIONS` uses.
 */
export function leaveTypeLabel(type: LeaveType): string {
  return translate(`leave.type.${type}`);
}

export function leaveStatusLabel(status: LeaveStatus): string {
  return translate(`leave.status.${status}`);
}

/**
 * Accent colour per type, as a hex the chip tints with (the calendar paints
 * with inline styles, because Tailwind would purge a class built at runtime).
 * Annual leave is the calm green of time off; sick leave the amber of
 * something unplanned; unpaid and special events stay neutral so a holiday
 * roster does not look like a traffic light.
 */
export const LEAVE_TYPE_COLOR: Record<LeaveType, string> = {
  ANNUAL: '#10b981',
  SICK: '#f59e0b',
  UNPAID: '#6b7280',
  SPECIAL_EVENT: '#8b5cf6',
  SHORT_ABSENCE: '#0ea5e9',
};

/** Status accent for the badges in the list and the admin table. */
export const LEAVE_STATUS_COLOR: Record<LeaveStatus, string> = {
  PENDING: '#f59e0b',
  APPROVED: '#10b981',
  REJECTED: '#ef4444',
  CANCELLED: '#9ca3af',
};

// --- Day arithmetic ---------------------------------------------------------
//
// The same rules the server applies, so the form can count a request's cost
// live as the dates are picked instead of waiting for a round trip. The
// holiday table is NOT duplicated here: callers pass the list the API gave
// them, so there is exactly one Romanian calendar in the system.

/** Shift a "YYYY-MM-DD" key by whole days, in UTC so no DST can bite. */
export function addDayKey(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + delta));
  const mm = String(t.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(t.getUTCDate()).padStart(2, '0');
  return `${t.getUTCFullYear()}-${mm}-${dd}`;
}

/** Saturday or Sunday. */
export function isWeekendKey(day: string): boolean {
  const [y, m, d] = day.split('-').map(Number);
  const js = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  return js === 0 || js === 6;
}

/** Every day key in the inclusive range; empty when the range is inverted. */
export function eachDayKey(from: string, to: string): string[] {
  if (!from || !to || to < from) return [];
  const days: string[] = [];
  for (let day = from; day <= to; day = addDayKey(day, 1)) days.push(day);
  return days;
}

/**
 * What the range costs in working days, given the legal holidays for the years
 * it spans. Weekends and holidays are free.
 */
export function countWorkingDays(from: string, to: string, holidays: Set<string>): number {
  return eachDayKey(from, to).filter((day) => !isWeekendKey(day) && !holidays.has(day)).length;
}

/** The holiday list from the API, as the Set the counter wants. */
export function holidayKeySet(holidays: PublicHoliday[]): Set<string> {
  return new Set(holidays.map((h) => h.day));
}

/** The years a range touches, so the form knows which holiday tables to fetch. */
export function yearsInRange(from: string, to: string): { year: number; through: number } {
  const first = Number(from.slice(0, 4));
  const last = Number((to || from).slice(0, 4));
  return Number.isFinite(first) && Number.isFinite(last) && last >= first
    ? { year: first, through: last }
    : { year: new Date().getFullYear(), through: new Date().getFullYear() };
}

/**
 * "12 oct." or "12–16 oct. 2026" — how a range reads in a chip or a row. The
 * year is dropped when the range sits inside `referenceYear`, because a
 * calendar already says which year you are looking at.
 */
export function formatLeaveRange(
  startDate: string,
  endDate: string,
  locale: string,
  referenceYear?: number,
): string {
  const sameYear =
    referenceYear !== undefined &&
    startDate.slice(0, 4) === String(referenceYear) &&
    endDate.slice(0, 4) === String(referenceYear);
  const options: Intl.DateTimeFormatOptions = {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
    ...(sameYear ? {} : { year: 'numeric' }),
  };
  // Midday UTC keeps the rendered day stable whatever the viewer's offset.
  const at = (day: string) => new Date(`${day}T12:00:00Z`);
  const fmt = new Intl.DateTimeFormat(locale, options);
  if (startDate === endDate) return fmt.format(at(startDate));
  return `${fmt.format(at(startDate))} – ${fmt.format(at(endDate))}`;
}

/** "3 zile" / "4 ore" — how long the absence is, in words. */
export function formatLeaveLength(
  type: LeaveType,
  workingDays: number,
  hours: number | null,
): string {
  if (type === 'SHORT_ABSENCE') {
    return translate('leave.lengthHours', undefined, { count: String(hours ?? 0) });
  }
  return translate(workingDays === 1 ? 'leave.lengthDay' : 'leave.lengthDays', undefined, {
    count: String(workingDays),
  });
}
